import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Mail, Users, Send, Settings, BarChart3, Plus, Search, X, ChevronLeft, ChevronRight, ChevronDown, Pause, Play, Clock, CheckCircle2, AlertCircle, Pencil, Trash2, MailPlus, MessageSquare, FileText, Phone, Filter, Eye, MousePointerClick, Activity } from 'lucide-react'
import { useEmDashboard, useEmCampaigns, useEmCampaign, useEmCampaignLogs, useEmTemplate, useEmTemplates, useEmLists, useEmAccounts, useEmWaAccounts, useCreateEmCampaign, useUpdateEmCampaign, useScheduleEmCampaign, usePauseEmCampaign, useResumeEmCampaign, useDeleteEmCampaign, useEmRecentLogs, useEmRecentWaLogs, type EmCampaign, type EmCampaignLog, type EmWhatsAppLog } from '@/hooks/useEmailMarketing'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'

const STATUS_CFG: Record<string, { label: string; color: string; icon: any }> = {
  editing: { label: 'Edição', color: '#64748b', icon: Pencil },
  scheduled: { label: 'Agendado', color: '#f59e0b', icon: Clock },
  sending: { label: 'Em Disparo', color: '#3b82f6', icon: Send },
  paused: { label: 'Pausado', color: '#f97316', icon: Pause },
  completed: { label: 'Concluído', color: '#10b981', icon: CheckCircle2 },
  failed: { label: 'Falhou', color: '#ef4444', icon: AlertCircle },
}

function StatCard({ icon: Icon, label, value, color }: { icon: any; label: string; value: string | number; color: string }) {
  return (
    <div className="bg-card border rounded-lg p-4 flex flex-col items-center gap-1">
      <Icon className="w-5 h-5" style={{ color }} />
      <span className="text-xl font-bold">{value}</span>
      <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wide">{label}</span>
    </div>
  )
}

export default function EmailMarketingPage() {
  const navigate = useNavigate()
  const dashboard = useEmDashboard()
  const campaigns = useEmCampaigns({ page: 1, per_page: 50 })
  const templates = useEmTemplates()
  const lists = useEmLists()
  const accounts = useEmAccounts()

  const createCampaignMut = useCreateEmCampaign()
  const updateCampaignMut = useUpdateEmCampaign()
  const scheduleMut = useScheduleEmCampaign()
  const pauseMut = usePauseEmCampaign()
  const resumeMut = useResumeEmCampaign()
  const deleteMut = useDeleteEmCampaign()
  const [confirmDeleteCampaign, setConfirmDeleteCampaign] = useState<EmCampaign | null>(null)

  // Log section state
  const [logOpen, setLogOpen] = useState(false)
  const [logTab, setLogTab] = useState<'email' | 'whatsapp'>('email')
  const [logSearch, setLogSearch] = useState('')
  const [logStatusFilter, setLogStatusFilter] = useState('')
  const [logPage, setLogPage] = useState(1)
  const recentLogs = useEmRecentLogs({ search: logSearch || undefined, status: logStatusFilter || undefined, page: logPage, per_page: 83 })
  const recentWaLogs = useEmRecentWaLogs({ search: logSearch || undefined, status: logStatusFilter || undefined, page: logPage, per_page: 83 })

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [showCampaignModal, setShowCampaignModal] = useState(false)
  const [editingCampaignId, setEditingCampaignId] = useState<string | null>(null)
  const [detailCampaignId, setDetailCampaignId] = useState<string | null>(null)
  const [campaignForm, setCampaignForm] = useState({ name: '', template_id: '', list_id: '', account_id: '', channels: 'email', scheduled_start: '', throttle_per_min: 10, dispatch_type: 'immediate' as 'immediate' | 'scheduled' })

  // Auto-refresh every 5s when campaigns are sending/scheduled
  const hasActive = (campaigns.data?.items || []).some(c => c.status === 'sending' || c.status === 'scheduled')
  useEffect(() => {
    if (!hasActive) return
    const interval = setInterval(() => { campaigns.refetch(); dashboard.refetch() }, 5000)
    return () => clearInterval(interval)
  }, [hasActive])

  const d = dashboard.data
  const items = (campaigns.data?.items || []).filter(c => {
    if (statusFilter && c.status !== statusFilter) return false
    if (search) {
      const s = search.toLowerCase()
      return (c.name?.toLowerCase().includes(s) || c.template_name?.toLowerCase().includes(s) || c.list_name?.toLowerCase().includes(s))
    }
    return true
  })

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Email Marketing</h1>
          <p className="text-sm text-muted-foreground">Campanhas de email e WhatsApp</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => navigate('/email-marketing/templates')} className="px-3 py-2 text-sm border rounded-lg hover:bg-accent/50 flex items-center gap-2">
            <MailPlus className="w-4 h-4" /> Templates
          </button>
          <button onClick={() => navigate('/email-marketing/lists')} className="px-3 py-2 text-sm border rounded-lg hover:bg-accent/50 flex items-center gap-2">
            <Users className="w-4 h-4" /> Listas
          </button>
          <button onClick={() => navigate('/email-marketing/accounts')} className="px-3 py-2 text-sm border rounded-lg hover:bg-accent/50 flex items-center gap-2">
            <Settings className="w-4 h-4" /> Contas
          </button>
        </div>
      </div>

      {/* KPIs */}
      {d && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard icon={Send} label="Campanhas" value={d.total_campaigns} color="#3b82f6" />
          <StatCard icon={Mail} label="Emails Enviados" value={d.total_emails_sent.toLocaleString()} color="#8b5cf6" />
          <StatCard icon={BarChart3} label="Taxa Abertura" value={`${(d.open_rate * 100).toFixed(1)}%`} color="#10b981" />
          <StatCard icon={MessageSquare} label="WA Enviados" value={d.total_wa_sent.toLocaleString()} color="#25d366" />
          <StatCard icon={Users} label="Contatos" value={d.total_contacts.toLocaleString()} color="#f59e0b" />
        </div>
      )}

      {/* Campaigns Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold">Campanhas</h2>
          <button onClick={() => { setEditingCampaignId(null); setCampaignForm({ name: '', template_id: '', list_id: '', account_id: '', channels: 'email', scheduled_start: '', throttle_per_min: 10, dispatch_type: 'immediate' }); setShowCampaignModal(true) }} className="px-3 py-2 text-sm bg-primary text-primary-foreground rounded-lg flex items-center gap-2 hover:opacity-90">
            <Plus className="w-4 h-4" /> Nova Campanha
          </button>
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar campanha..." className="w-full pl-9 pr-8 py-2 text-sm bg-background border rounded-md" />
            {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2"><X className="w-4 h-4 text-muted-foreground" /></button>}
          </div>
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 text-sm bg-background border rounded-md">
            <option value="">Todos</option>
            {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>

        {/* Grid */}
        <div className="bg-card border rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/50 text-muted-foreground text-xs uppercase">
                <th className="p-3 text-left font-medium">Campanha</th>
                <th className="p-3 text-left font-medium hidden md:table-cell">Template</th>
                <th className="p-3 text-left font-medium hidden lg:table-cell">Lista</th>
                <th className="p-3 text-center font-medium">Canal</th>
                <th className="p-3 text-center font-medium">Status</th>
                <th className="p-3 text-center font-medium hidden md:table-cell">Enviados</th>
                <th className="p-3 text-center font-medium hidden lg:table-cell">Abertos</th>
                <th className="p-3 text-center font-medium">Ações</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.isLoading ? (
                <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Carregando...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">
                  {search || statusFilter ? 'Nenhuma campanha encontrada' : 'Nenhuma campanha criada ainda'}
                </td></tr>
              ) : items.map(c => {
                const cfg = STATUS_CFG[c.status] || STATUS_CFG.editing
                const Icon = cfg.icon
                return (
                  <tr key={c.id} className="border-t hover:bg-accent/30 cursor-pointer" onClick={() => setDetailCampaignId(c.id)}>
                    <td className="p-3">
                      <div className="font-medium">{c.name}</div>
                      {c.scheduled_start && <div className="text-xs text-muted-foreground">{new Date(c.scheduled_start).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</div>}
                    </td>
                    <td className="p-3 text-muted-foreground hidden md:table-cell">{c.template_name || '—'}</td>
                    <td className="p-3 text-muted-foreground hidden lg:table-cell">{c.list_name || '—'} ({c.total_recipients})</td>
                    <td className="p-3 text-center">
                      <span className="text-xs font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: c.channels === 'both' ? 'rgba(139,92,246,.15)' : c.channels === 'whatsapp' ? 'rgba(37,211,102,.15)' : 'rgba(59,130,246,.15)', color: c.channels === 'both' ? '#8b5cf6' : c.channels === 'whatsapp' ? '#25d366' : '#3b82f6' }}>
                        {c.channels === 'both' ? 'Email+WA' : c.channels === 'whatsapp' ? 'WhatsApp' : 'Email'}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: cfg.color + '20', color: cfg.color }}>
                        <Icon className="w-3 h-3" /> {cfg.label}
                      </span>
                    </td>
                    <td className="p-3 hidden md:table-cell">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{
                            width: `${c.total_recipients > 0 ? Math.round((c.sent_count / c.total_recipients) * 100) : 0}%`,
                            backgroundColor: c.status === 'completed' ? '#10b981' : c.status === 'sending' ? '#3b82f6' : c.status === 'failed' ? '#ef4444' : '#64748b',
                          }} />
                        </div>
                        <span className="text-xs text-muted-foreground w-16 text-right">{c.sent_count}/{c.total_recipients}</span>
                      </div>
                    </td>
                    <td className="p-3 text-center hidden lg:table-cell">{c.sent_count > 0 ? `${((c.opened_count / c.sent_count) * 100).toFixed(0)}%` : '\u2014'}</td>
                    <td className="p-3 text-center" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-1">
                        {(c.status === 'editing' || c.status === 'scheduled') && (
                          <button onClick={() => {
                            setEditingCampaignId(c.id)
                            setCampaignForm({
                              name: c.name, template_id: c.template_id, list_id: c.list_id,
                              account_id: c.account_id || '',
                              channels: c.channels, scheduled_start: c.scheduled_start?.slice(0,16) || '',
                              throttle_per_min: c.throttle_per_min || 10,
                              dispatch_type: c.scheduled_start ? 'scheduled' : 'immediate',
                            })
                            setShowCampaignModal(true)
                          }} className="p-1 rounded hover:bg-accent" title="Editar"><Pencil className="w-4 h-4 text-blue-400" /></button>
                        )}
                        {c.status === 'editing' && (
                          <button onClick={() => scheduleMut.mutate(c.id)} className="p-1 rounded hover:bg-accent" title="Agendar"><Clock className="w-4 h-4 text-amber-500" /></button>
                        )}
                        {c.status === 'sending' && (
                          <button onClick={() => pauseMut.mutate(c.id)} className="p-1 rounded hover:bg-accent" title="Pausar"><Pause className="w-4 h-4 text-orange-500" /></button>
                        )}
                        {c.status === 'paused' && (
                          <button onClick={() => resumeMut.mutate(c.id)} className="p-1 rounded hover:bg-accent" title="Retomar"><Play className="w-4 h-4 text-green-500" /></button>
                        )}
                        {(c.status === 'editing' || c.status === 'scheduled' || c.status === 'failed' || c.status === 'completed') && (
                          <button onClick={() => setConfirmDeleteCampaign(c)} className="p-1 rounded hover:bg-accent" title="Excluir"><Trash2 className="w-4 h-4 text-red-500" /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* LOG DOS DISPAROS */}
      <div className="mt-2">
        <button
          onClick={() => { setLogOpen(!logOpen); if (!logOpen) { recentLogs.refetch(); recentWaLogs.refetch() } }}
          className="flex items-center gap-2 w-full text-left px-4 py-3 bg-card border rounded-lg hover:bg-accent/30 transition-colors"
        >
          <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${logOpen ? 'rotate-90' : ''}`} />
          <FileText className="w-4 h-4 text-blue-400" />
          <span className="font-semibold text-sm uppercase tracking-wide">Log dos Disparos</span>
          <span className="text-xs text-muted-foreground ml-2">({(recentLogs.data?.length || 0)} emails, {(recentWaLogs.data?.length || 0)} WA)</span>
        </button>

        {logOpen && (
          <div className="mt-2 bg-card border rounded-lg overflow-hidden">
            {/* Tabs */}
            <div className="flex items-center gap-0 border-b">
              <button onClick={() => { setLogTab('email'); setLogPage(1); setLogStatusFilter('') }} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${logTab === 'email' ? 'border-blue-500 text-blue-500' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                <Mail className="w-4 h-4 inline mr-1.5" />Email ({recentLogs.data?.length || 0})
              </button>
              <button onClick={() => { setLogTab('whatsapp'); setLogPage(1); setLogStatusFilter('') }} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${logTab === 'whatsapp' ? 'border-green-500 text-green-500' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
                <MessageSquare className="w-4 h-4 inline mr-1.5" />WhatsApp ({recentWaLogs.data?.length || 0})
              </button>
              <div className="flex-1" />
              {/* Filters */}
              <div className="flex items-center gap-2 px-3">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
                  <input value={logSearch} onChange={e => { setLogSearch(e.target.value); setLogPage(1) }} placeholder="Buscar..." className="pl-8 pr-3 py-1.5 text-xs bg-background border rounded-md w-48" />
                </div>
                <select value={logStatusFilter} onChange={e => { setLogStatusFilter(e.target.value); setLogPage(1) }} className="px-2 py-1.5 text-xs bg-background border rounded-md">
                  <option value="">Status</option>
                  <option value="sent">Enviado</option>
                  <option value="delivered">Entregue</option>
                  <option value="opened">Aberto</option>
                  <option value="clicked">Clicado</option>
                  <option value="bounced">Bounce</option>
                  <option value="failed">Falhou</option>
                  {logTab === 'whatsapp' && <option value="read">Lido</option>}
                </select>
              </div>
            </div>

            {/* Email Log Table */}
            {logTab === 'email' && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/50 text-muted-foreground uppercase">
                      <th className="p-2.5 text-left font-medium">Email</th>
                      <th className="p-2.5 text-left font-medium hidden md:table-cell">Assunto</th>
                      <th className="p-2.5 text-center font-medium">Status</th>
                      <th className="p-2.5 text-center font-medium hidden md:table-cell">Enviado</th>
                      <th className="p-2.5 text-center font-medium hidden lg:table-cell">Aberto</th>
                      <th className="p-2.5 text-center font-medium hidden lg:table-cell">Clicado</th>
                      <th className="p-2.5 text-left font-medium hidden xl:table-cell">Erro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentLogs.isLoading ? (
                      <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Carregando...</td></tr>
                    ) : !recentLogs.data?.length ? (
                      <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Nenhum log encontrado</td></tr>
                    ) : recentLogs.data.map((log: EmCampaignLog) => (
                      <tr key={log.id} className="border-t hover:bg-accent/20">
                        <td className="p-2.5 font-mono">{log.email_to}</td>
                        <td className="p-2.5 text-muted-foreground hidden md:table-cell max-w-[200px] truncate">{log.subject || '—'}</td>
                        <td className="p-2.5 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            log.status === 'sent' ? 'bg-blue-500/15 text-blue-400' :
                            log.status === 'delivered' ? 'bg-green-500/15 text-green-400' :
                            log.status === 'opened' ? 'bg-emerald-500/15 text-emerald-400' :
                            log.status === 'clicked' ? 'bg-purple-500/15 text-purple-400' :
                            log.status === 'bounced' ? 'bg-orange-500/15 text-orange-400' :
                            log.status === 'failed' ? 'bg-red-500/15 text-red-400' :
                            'bg-gray-500/15 text-gray-400'
                          }`}>
                            {log.status === 'sent' ? 'Enviado' : log.status === 'delivered' ? 'Entregue' : log.status === 'opened' ? 'Aberto' : log.status === 'clicked' ? 'Clicado' : log.status === 'bounced' ? 'Bounce' : log.status === 'failed' ? 'Falhou' : log.status}
                          </span>
                        </td>
                        <td className="p-2.5 text-center text-muted-foreground hidden md:table-cell">{log.sent_at ? new Date(log.sent_at).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'}</td>
                        <td className="p-2.5 text-center hidden lg:table-cell">{log.opened_at ? new Date(log.opened_at).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'}</td>
                        <td className="p-2.5 text-center hidden lg:table-cell">{log.clicked_at ? new Date(log.clicked_at).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'}</td>
                        <td className="p-2.5 text-red-400 hidden xl:table-cell max-w-[150px] truncate">{log.error_message || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* WhatsApp Log Table */}
            {logTab === 'whatsapp' && (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-muted/50 text-muted-foreground uppercase">
                      <th className="p-2.5 text-left font-medium">Telefone</th>
                      <th className="p-2.5 text-left font-medium hidden md:table-cell">Mensagem</th>
                      <th className="p-2.5 text-center font-medium">Status</th>
                      <th className="p-2.5 text-center font-medium hidden md:table-cell">Enviado</th>
                      <th className="p-2.5 text-center font-medium hidden lg:table-cell">Entregue</th>
                      <th className="p-2.5 text-center font-medium hidden lg:table-cell">Lido</th>
                      <th className="p-2.5 text-left font-medium hidden xl:table-cell">Erro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentWaLogs.isLoading ? (
                      <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Carregando...</td></tr>
                    ) : !recentWaLogs.data?.length ? (
                      <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">Nenhum log encontrado</td></tr>
                    ) : recentWaLogs.data.map((log: EmWhatsAppLog) => (
                      <tr key={log.id} className="border-t hover:bg-accent/20">
                        <td className="p-2.5 font-mono">{log.phone_to}</td>
                        <td className="p-2.5 text-muted-foreground hidden md:table-cell max-w-[200px] truncate">{log.message_text || '—'}</td>
                        <td className="p-2.5 text-center">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                            log.status === 'sent' ? 'bg-blue-500/15 text-blue-400' :
                            log.status === 'delivered' ? 'bg-green-500/15 text-green-400' :
                            log.status === 'read' ? 'bg-emerald-500/15 text-emerald-400' :
                            log.status === 'failed' ? 'bg-red-500/15 text-red-400' :
                            'bg-gray-500/15 text-gray-400'
                          }`}>
                            {log.status === 'sent' ? 'Enviado' : log.status === 'delivered' ? 'Entregue' : log.status === 'read' ? 'Lido' : log.status === 'failed' ? 'Falhou' : log.status}
                          </span>
                        </td>
                        <td className="p-2.5 text-center text-muted-foreground hidden md:table-cell">{log.sent_at ? new Date(log.sent_at).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'}</td>
                        <td className="p-2.5 text-center hidden lg:table-cell">{log.delivered_at ? new Date(log.delivered_at).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'}</td>
                        <td className="p-2.5 text-center hidden lg:table-cell">{log.read_at ? new Date(log.read_at).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', hour:'2-digit', minute:'2-digit' }) : '—'}</td>
                        <td className="p-2.5 text-red-400 hidden xl:table-cell max-w-[150px] truncate">{log.error_message || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Campaign Modal (New/Edit) */}
      {showCampaignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowCampaignModal(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div className="relative w-full max-w-lg bg-card border rounded-xl shadow-2xl p-6" onClick={e => e.stopPropagation()}>
            <h3 className="font-semibold mb-4">{editingCampaignId ? 'Editar Campanha' : 'Nova Campanha'}</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Nome *</label>
                <input value={campaignForm.name} onChange={e => setCampaignForm({...campaignForm, name: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" placeholder="Black Friday 2026" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Template *</label>
                  <select value={campaignForm.template_id} onChange={e => setCampaignForm({...campaignForm, template_id: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md">
                    <option value="">Selecionar...</option>
                    {(templates.data || []).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Lista *</label>
                  <select value={campaignForm.list_id} onChange={e => setCampaignForm({...campaignForm, list_id: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md">
                    <option value="">Selecionar...</option>
                    {(lists.data || []).map(l => <option key={l.id} value={l.id}>{l.name} ({l.contact_count})</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Canal de disparo</label>
                <select value={campaignForm.channels} onChange={e => setCampaignForm({...campaignForm, channels: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md">
                  <option value="email">Email</option>
                  <option value="whatsapp">WhatsApp</option>
                  <option value="both">Email + WhatsApp</option>
                </select>
              </div>
              {(campaignForm.channels === 'email' || campaignForm.channels === 'both') && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">
                    Conta de E-mail {campaignForm.channels !== 'whatsapp' && '*'}
                  </label>
                  <select
                    value={campaignForm.account_id}
                    onChange={e => setCampaignForm({...campaignForm, account_id: e.target.value})}
                    className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md"
                  >
                    <option value="">{accounts.isLoading ? 'Carregando contas…' : 'Usar conta padrão'}</option>
                    {(accounts.data || []).filter(a => a.is_active).map(a => (
                      <option key={a.id} value={a.id}>
                        {a.name} ({a.email}){a.is_default ? ' • padrão' : ''} — {a.sent_today}/{a.daily_limit} hoje
                      </option>
                    ))}
                  </select>
                  {!accounts.isLoading && (accounts.data || []).filter(a => a.is_active).length === 0 && (
                    <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> Nenhuma conta de e-mail ativa cadastrada.
                    </p>
                  )}
                </div>
              )}
              {/* Dispatch type selector */}
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1.5 block">Tipo de disparo</label>
                <div className="flex gap-1 bg-muted/50 p-1 rounded-lg">
                  <button onClick={() => setCampaignForm({...campaignForm, dispatch_type: 'immediate', scheduled_start: ''})} className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${campaignForm.dispatch_type === 'immediate' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'}`}>
                    Imediato
                  </button>
                  <button onClick={() => setCampaignForm({...campaignForm, dispatch_type: 'scheduled'})} className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${campaignForm.dispatch_type === 'scheduled' ? 'bg-card shadow text-foreground' : 'text-muted-foreground'}`}>
                    Agendado
                  </button>
                </div>
              </div>
              {campaignForm.dispatch_type === 'scheduled' && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground">Data e hora do disparo *</label>
                  <input type="datetime-local" value={campaignForm.scheduled_start} onChange={e => setCampaignForm({...campaignForm, scheduled_start: e.target.value})} className="w-full mt-1 px-3 py-2 text-sm bg-background border rounded-md" />
                </div>
              )}
              {campaignForm.dispatch_type === 'immediate' && (
                <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-500/10 rounded-lg p-2.5">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  O disparo iniciará imediatamente após salvar. Horário do servidor: America/Sao_Paulo (BRT).
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setShowCampaignModal(false)} className="px-4 py-2 text-sm border rounded-lg">Cancelar</button>
              <button
                onClick={async () => {
                  try {
                    // Build BRT timestamp for immediate dispatch
                    const now = new Date()
                    const brt = new Date(now.getTime() - (now.getTimezoneOffset() * 60000))
                    const brtIso = brt.toISOString().slice(0, 19)

                    const payload: any = {
                      name: campaignForm.name,
                      template_id: campaignForm.template_id,
                      list_id: campaignForm.list_id,
                      channels: campaignForm.channels,
                      throttle_per_min: campaignForm.throttle_per_min,
                    }
                    if (campaignForm.account_id && (campaignForm.channels === 'email' || campaignForm.channels === 'both')) {
                      payload.account_id = campaignForm.account_id
                    }
                    if (campaignForm.dispatch_type === 'scheduled' && campaignForm.scheduled_start) {
                      payload.scheduled_start = campaignForm.scheduled_start
                    } else if (campaignForm.dispatch_type === 'immediate') {
                      payload.scheduled_start = brtIso
                    }

                    let campId: string
                    if (editingCampaignId) {
                      await updateCampaignMut.mutateAsync({ id: editingCampaignId, ...payload })
                      campId = editingCampaignId
                    } else {
                      const created: any = await createCampaignMut.mutateAsync(payload)
                      campId = created?.id || created?.data?.id
                    }
                    // Schedule (editing -> scheduled, Celery will pick up)
                    if (campId) {
                      try { await scheduleMut.mutateAsync(campId) } catch(e) { console.warn('Schedule call:', e) }
                    }
                    setShowCampaignModal(false)
                    campaigns.refetch()
                  } catch (e: any) {
                    alert('Erro: ' + (e?.response?.data?.detail || e?.message || 'Falha'))
                  }
                }}
                disabled={!campaignForm.name || !campaignForm.template_id || !campaignForm.list_id || (campaignForm.dispatch_type === 'scheduled' && !campaignForm.scheduled_start)}
                className="px-4 py-2 text-sm bg-primary text-primary-foreground rounded-lg disabled:opacity-40"
              >
                {editingCampaignId ? 'Salvar' : (campaignForm.dispatch_type === 'immediate' ? 'Disparar Agora' : 'Agendar Campanha')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmDeleteCampaign}
        title="Excluir campanha?"
        message={
          confirmDeleteCampaign?.status === 'completed' || confirmDeleteCampaign?.status === 'failed'
            ? `A campanha "${confirmDeleteCampaign?.name || ''}" e todo o seu histórico de envios (aberturas, cliques, logs) serão removidos. Esta ação não pode ser desfeita.`
            : `A campanha "${confirmDeleteCampaign?.name || ''}" será removida. Esta ação não pode ser desfeita.`
        }
        confirmLabel="Excluir"
        variant="destructive"
        isLoading={deleteMut.isPending}
        onConfirm={async () => {
          if (!confirmDeleteCampaign) return
          await deleteMut.mutateAsync(confirmDeleteCampaign.id)
          setConfirmDeleteCampaign(null)
        }}
        onCancel={() => setConfirmDeleteCampaign(null)}
      />

      {detailCampaignId && (
        <CampaignDetailModal campaignId={detailCampaignId} onClose={() => setDetailCampaignId(null)} />
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────
// CampaignDetailModal — visão completa: métricas, conteúdo e logs
// ──────────────────────────────────────────────────────────────

function MetricBox({ icon: Icon, label, value, sub, color }: { icon: any; label: string; value: number | string; sub?: string; color: string }) {
  return (
    <div className="bg-muted/30 border rounded-lg p-3">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">{label}</span>
      </div>
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  )
}

function CampaignDetailModal({ campaignId, onClose }: { campaignId: string; onClose: () => void }) {
  const campaignQ = useEmCampaign(campaignId)
  const c = campaignQ.data
  const logsQ = useEmCampaignLogs(campaignId, { page: 1, per_page: 200 })
  const templateQ = useEmTemplate(c?.template_id || '')
  const updateMut = useUpdateEmCampaign()

  const [editingName, setEditingName] = useState(false)
  const [nameDraft, setNameDraft] = useState('')

  const startEdit = () => {
    setNameDraft(c?.name || '')
    setEditingName(true)
  }
  const saveName = async () => {
    const trimmed = nameDraft.trim()
    if (!c || !trimmed || trimmed === c.name) { setEditingName(false); return }
    try {
      await updateMut.mutateAsync({ id: c.id, name: trimmed })
      setEditingName(false)
    } catch (e: any) {
      alert(e?.response?.data?.detail || 'Erro ao salvar nome')
    }
  }

  // Auto-refresh enquanto a campanha estiver ativa
  useEffect(() => {
    if (!c) return
    if (c.status !== 'sending' && c.status !== 'scheduled') return
    const interval = setInterval(() => { campaignQ.refetch(); logsQ.refetch() }, 5000)
    return () => clearInterval(interval)
  }, [c?.status, campaignQ, logsQ])

  const total = c?.total_recipients || 0
  const sent = c?.sent_count || 0
  const failed = c?.failed_count || 0
  const opened = c?.opened_count || 0
  const clicked = c?.clicked_count || 0
  const pctOpen = sent > 0 ? Math.round((opened / sent) * 100) : 0
  const pctClick = sent > 0 ? Math.round((clicked / sent) * 100) : 0
  const cfg = c ? (STATUS_CFG[c.status] || STATUS_CFG.editing) : STATUS_CFG.editing
  const StatusIcon = cfg.icon
  const logs = logsQ.data || []

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="bg-card border rounded-lg shadow-xl w-full max-w-6xl max-h-[92vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between p-5 border-b sticky top-0 bg-card z-10">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              {editingName ? (
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  <input
                    autoFocus
                    type="text"
                    value={nameDraft}
                    onChange={e => setNameDraft(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') { e.preventDefault(); saveName() }
                      if (e.key === 'Escape') { e.preventDefault(); setEditingName(false) }
                    }}
                    className="text-xl font-bold bg-background border-2 border-blue-500 rounded px-2 py-1 flex-1 min-w-0 focus:outline-none"
                    disabled={updateMut.isPending}
                  />
                  <button onClick={saveName} disabled={updateMut.isPending} className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded hover:opacity-90 disabled:opacity-50">
                    {updateMut.isPending ? 'Salvando...' : 'Salvar'}
                  </button>
                  <button onClick={() => setEditingName(false)} disabled={updateMut.isPending} className="px-3 py-1.5 text-sm border rounded hover:bg-accent">
                    Cancelar
                  </button>
                </div>
              ) : (
                <button
                  onClick={startEdit}
                  disabled={!c}
                  className="group flex items-center gap-2 text-left max-w-full min-w-0"
                  title="Clique para renomear"
                >
                  <h2 className="text-xl font-bold truncate">{c?.name || 'Carregando...'}</h2>
                  {c && <Pencil className="w-3.5 h-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />}
                </button>
              )}
              {c && !editingName && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: cfg.color + '20', color: cfg.color }}>
                  <StatusIcon className="w-3 h-3" /> {cfg.label}
                </span>
              )}
            </div>
            {c && (
              <div className="text-sm text-muted-foreground mt-2 flex gap-4 flex-wrap">
                <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> {c.list_name || '—'}</span>
                <span className="flex items-center gap-1.5"><FileText className="w-3.5 h-3.5" /> {c.template_name || '—'}</span>
                {c.scheduled_start && <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {new Date(c.scheduled_start).toLocaleString('pt-BR')}</span>}
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-2 rounded hover:bg-accent ml-4"><X className="w-5 h-5" /></button>
        </div>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-5 border-b">
          <MetricBox icon={Users} label="Destinatários" value={total} color="#64748b" />
          <MetricBox icon={Send} label="Enviados" value={sent} sub={total > 0 ? `${Math.round((sent/total)*100)}% da lista` : undefined} color="#3b82f6" />
          <MetricBox icon={AlertCircle} label="Falhas" value={failed} color="#ef4444" />
          <MetricBox icon={Eye} label="Aberturas" value={opened} sub={sent > 0 ? `${pctOpen}% taxa abertura` : undefined} color="#10b981" />
          <MetricBox icon={MousePointerClick} label="Cliques" value={clicked} sub={sent > 0 ? `${pctClick}% taxa clique` : undefined} color="#f59e0b" />
        </div>

        {/* Conteúdo + Logs */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 p-5">
          {/* Conteúdo enviado */}
          <div className="border rounded-lg overflow-hidden flex flex-col">
            <div className="px-4 py-2.5 bg-muted/30 border-b text-sm font-semibold flex items-center gap-2">
              <Mail className="w-4 h-4 text-blue-500" /> Conteúdo enviado
            </div>
            <div className="p-4 space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Assunto</div>
                <div className="font-medium">{templateQ.data?.subject || (templateQ.isLoading ? 'Carregando...' : '—')}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mb-1">Corpo do e-mail</div>
                {templateQ.data?.body_html ? (
                  <iframe
                    srcDoc={templateQ.data.body_html}
                    className="w-full h-[500px] border rounded bg-white"
                    sandbox=""
                    title="Preview do conteúdo da campanha"
                  />
                ) : (
                  <div className="text-sm text-muted-foreground py-8 text-center">
                    {templateQ.isLoading ? 'Carregando preview...' : 'Sem conteúdo para preview.'}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Logs por destinatário */}
          <div className="border rounded-lg overflow-hidden flex flex-col">
            <div className="px-4 py-2.5 bg-muted/30 border-b text-sm font-semibold flex items-center gap-2">
              <Activity className="w-4 h-4 text-purple-500" /> Disparos por destinatário
              <span className="ml-auto text-xs text-muted-foreground font-normal">{logs.length} {logs.length === 1 ? 'registro' : 'registros'}</span>
            </div>
            <div className="overflow-y-auto" style={{ maxHeight: '550px' }}>
              {logsQ.isLoading ? (
                <div className="p-6 text-center text-sm text-muted-foreground">Carregando logs...</div>
              ) : logs.length === 0 ? (
                <div className="p-6 text-center text-sm text-muted-foreground">Nenhum disparo registrado.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b sticky top-0">
                    <tr>
                      <th className="p-2 text-left font-medium text-xs uppercase tracking-wide">Destinatário</th>
                      <th className="p-2 text-left font-medium text-xs uppercase tracking-wide">Status</th>
                      <th className="p-2 text-left font-medium text-xs uppercase tracking-wide hidden md:table-cell">Enviado</th>
                      <th className="p-2 text-center font-medium text-xs uppercase tracking-wide">Aberto</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map(l => {
                      const statusColor = l.status === 'sent' ? '#3b82f6' : l.status === 'opened' ? '#10b981' : l.status === 'clicked' ? '#f59e0b' : l.status === 'failed' ? '#ef4444' : '#64748b'
                      return (
                        <tr key={l.id} className="border-t">
                          <td className="p-2 truncate max-w-[200px]" title={l.email_to}>{l.email_to}</td>
                          <td className="p-2">
                            <span className="inline-block px-2 py-0.5 rounded-full text-[11px] font-medium" style={{ backgroundColor: statusColor + '20', color: statusColor }}>
                              {l.status}
                            </span>
                            {l.error_message && (
                              <div className="text-[10px] text-red-500 mt-0.5 truncate max-w-[180px]" title={l.error_message}>{l.error_message}</div>
                            )}
                          </td>
                          <td className="p-2 text-xs text-muted-foreground hidden md:table-cell">
                            {l.sent_at ? new Date(l.sent_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—'}
                          </td>
                          <td className="p-2 text-center text-xs">
                            {l.opened_count > 0 ? (
                              <span className="text-green-600 font-medium">{l.opened_count}x</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
